import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import { environment } from '../../../environments/environment';
import { AccountInvitationState, AccountMembersOverview } from '../../models/AccountMember';
import { ownErrorToastOptions, validationMessage } from '../interceptors/http-error.interceptor';

/**
 * Frase para a tela a partir da recusa da gestão de membros. Convidar, reenviar
 * e remover marcam `OWN_ERROR_TOAST` e dão o próprio aviso: a recusa vem como
 * `{ error, message }`, e o toast genérico do 422 juntaria o código e a frase
 * ("email_taken, Este e-mail já está cadastrado.") — por isso não dá para usar
 * o `ownErrorMessage` do interceptor, que achata o corpo inteiro no 422.
 *
 * `null` no 401, como no `ownErrorMessage`: esse o interceptor avisa sempre (e
 * derruba a sessão quando o token morreu). A tela não repete o aviso.
 */
export function accountMemberErrorMessage(error: HttpErrorResponse): string | null {
  if (error?.status === 401) {
    return null;
  }

  const body = error?.error;

  if (body && typeof body === 'object') {
    if (body.errors) {
      return validationMessage(body);
    }

    if (typeof body.message === 'string' && body.message) {
      return body.message;
    }
  }

  return 'Não foi possível concluir agora. Tente novamente.';
}

/**
 * Gestão de membros da conta (Multiusuários v1.0, CU-17tm466h1cm).
 *
 * A lista o membro também vê. Convidar, reenviar e remover são do dono: para o
 * membro a API devolve 403 `owner_only`.
 */
@Injectable({ providedIn: 'root' })
export class AccountMemberService {

  private readonly baseUrl = `${environment.apiUrl}/account`;

  constructor(private http: HttpClient) { }

  /**
   * 403 `multiuser_unavailable` quando o recurso está desligado ou o perfil não
   * tem. Quem lê dá o próprio aviso: o header fica calado (o item só não
   * aparece) e o modal mostra o estado de erro ou o toast de lista velha.
   */
  overview(): Observable<AccountMembersOverview> {
    return this.http.get<AccountMembersOverview>(`${this.baseUrl}/members`, ownErrorToastOptions());
  }

  invite(name: string, email: string): Observable<{ id: string }> {
    return this.http.post<{ id: string }>(`${this.baseUrl}/members`, { name, email }, ownErrorToastOptions());
  }

  resend(memberId: string): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/members/${memberId}/resend`, {}, ownErrorToastOptions());
  }

  /** Remove o membro ou cancela o convite pendente. */
  remove(memberId: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/members/${memberId}`, ownErrorToastOptions());
  }

  /** Página pública de aceite. */
  invitation(token: string): Observable<AccountInvitationState> {
    return this.http.get<AccountInvitationState>(`${this.baseUrl}/invitations/${encodeURIComponent(token)}`);
  }

  acceptInvitation(token: string, password: string): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/invitations/${encodeURIComponent(token)}`, {
      password,
      password_confirmation: password,
    });
  }
}
